import { useParams, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/common";
import { getGuide, GUIDE_CATEGORIES } from "@/data/guides";
import { ARTICLES } from "@/components/guides/articles";
import { ArrowLeft, Clock, BookOpen } from "lucide-react";

function categoryLabel(key) {
  return GUIDE_CATEGORIES.find((c) => c.key === key)?.label || "";
}

export default function GuideArticle() {
  const { slug } = useParams();
  const { can } = useAuth();
  const guide = getGuide(slug);
  const Article = ARTICLES[slug];

  // Unknown guide — friendly not-found.
  if (!guide || !Article) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/portal/guides" data-testid="guide-back-link">
            <ArrowLeft className="mr-2 h-4 w-4" /> All guides
          </Link>
        </Button>
        <EmptyState
          icon={BookOpen}
          title="Guide not found"
          description="That guide doesn’t exist or has moved. Head back to the Help & Guides hub."
          testId="guide-not-found"
        />
      </div>
    );
  }

  // RBAC: capability-gated guides (e.g. Producer setup) are off-limits to members.
  if (guide.cap && !can(guide.cap)) {
    return <Navigate to="/portal/guides" replace />;
  }

  const Icon = guide.icon;

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid={`guide-article-${guide.slug}`}>
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to="/portal/guides" data-testid="guide-back-link">
          <ArrowLeft className="mr-2 h-4 w-4" /> All guides
        </Link>
      </Button>

      <header className="space-y-3 border-b border-border pb-6">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-primary">
          <span>{categoryLabel(guide.category)}</span>
        </div>
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">{guide.title}</h1>
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> {guide.minutes} min read
            </p>
          </div>
        </div>
      </header>

      <article className="pb-4">
        <Article />
      </article>

      <div className="border-t border-border pt-6">
        <Button asChild variant="outline" size="sm">
          <Link to="/portal/guides" data-testid="guide-back-bottom">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to all guides
          </Link>
        </Button>
      </div>
    </div>
  );
}
